import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import browserHistory from 'react-router/lib/browserHistory';
import cn from 'classnames';

import Icon from '../../Icon';
import Button from '../../Button';
import Loader from '../../common/Loader';
import FieldsList from '../../common/FieldsList';
import transformErrors from '../utils/transformErrors';
import { AUTH, COMMON } from '../../../constants/strings';
import { isFlagler } from '../../../constants/brandConfig';
import { PROFILE_FIELDS_TYPES } from '../../../constants/formFields';
import { useSimpleForm } from '../../common/SimpleFormPopup/utils/useSimpleForm';
import styles from '../index.styles';
import { enhance } from './enhance';
import MetaHelmet from '../../AppHelmet/MetaHelmet';
import { usePageView } from '../../../hocs/usePageView';

const getRecoverPasswordTextTitle = primaryContactsEnabled => {
  return primaryContactsEnabled
    ? AUTH.actions.recoverPassword.textTitlePrimaryContacts
    : AUTH.actions.recoverPassword.textTitle;
};

const getFields = primaryContactsEnabled => {
  return primaryContactsEnabled
    ? [PROFILE_FIELDS_TYPES.USERNAME]
    : [PROFILE_FIELDS_TYPES.EMAIL_OR_PHONE];
};

const RecoverPassword = ({
  resetPassword,
  succeed,
  errors,
  getAction,
  primaryContactsEnabled,
  publicConstantsLoading,
  fetchPublicConstants,
  intl: { formatMessage },
}) => {
  useEffect(() => {
    fetchPublicConstants();
    getAction(null);
  }, []);

  usePageView('Recover Password');

  const fields = getFields(primaryContactsEnabled);

  const fieldName = primaryContactsEnabled ? 'username' : 'emailOrPhone';

  const {
    handleFormChange,
    validateErrors,
    clearErrors,
    formState,
    isValid,
  } = useSimpleForm(
    fields,
    fields,
    transformErrors(errors, formatMessage, fieldName),
  );

  const { emailOrPhone, username } = formState;

  const type =
    typeof emailOrPhone === 'string' && emailOrPhone.includes('@')
      ? 'email'
      : 'phone';

  const handleSubmit = () => {
    resetPassword({ login: primaryContactsEnabled ? username : emailOrPhone });
    clearErrors();
  };

  const handleBack = e => {
    e.preventDefault();
    browserHistory.goBack();
  };

  if (publicConstantsLoading) {
    return <Loader className="absolute-center" />;
  }

  return (
    <div className={styles.formWrapper}>
      <MetaHelmet title="pages.title.recoverPassword" />

      {!succeed && (
        <Fragment>
          <p className={styles.formTitle}>
            <FormattedMessage {...AUTH.titles.recoverPassword} />
          </p>
          <p
            className={cn(styles.formText, 'mb3 pt0')}
            style={{ textAlign: 'left' }}
          >
            <FormattedMessage
              {...getRecoverPasswordTextTitle(primaryContactsEnabled)}
            />
          </p>
          <FieldsList
            className={styles.fieldsList}
            fields={fields}
            formState={formState}
            validateErrors={validateErrors}
            onChange={handleFormChange}
            initialData={formState}
          />
          <Button
            className={cn(styles.acceptBtn, 'db mb2 w-100 lh-copy')}
            color="primary"
            raised
            onClick={handleSubmit}
            disabled={!isValid}
          >
            <FormattedMessage {...AUTH.submit.recoverPassword} />
          </Button>
          <Button
            className={cn(styles.cancelBtn, 'db w-100 lh-copy')}
            color="accent"
            type="button"
            onClick={handleBack}
          >
            <FormattedMessage {...COMMON.cancel} />
          </Button>
        </Fragment>
      )}
      {succeed && (
        <Fragment>
          {isFlagler && (
            <p className="md3 tc">
              <Icon name="icon-plane-line" size={64} />
            </p>
          )}
          <p className={styles.formTitle}>
            <FormattedMessage {...AUTH.titles.recoverConfirm} />
          </p>
          <p className={cn(styles.formText, 'mb0')}>
            {formatMessage(AUTH.actions.recoverConfirm[type].textTitle)}
          </p>
          <p className={cn(styles.formText, 'mb2 pt1 fw5')}>{emailOrPhone}</p>
          <p className={cn(styles.formText, 'pt2 mb0')}>
            {formatMessage(AUTH.actions.recoverConfirm[type].textConfirm)}
          </p>
          <Button
            className={cn(styles.acceptBtn, 'db mb3 w-100 lh-copy')}
            color="primary"
            raised
            onClick={handleBack}
          >
            <FormattedMessage {...AUTH.submit.recoverConfirm} />
          </Button>
        </Fragment>
      )}
    </div>
  );
};

RecoverPassword.propTypes = {
  errors: PropTypes.object,
  resetPassword: PropTypes.func.isRequired,
  succeed: PropTypes.bool,
  getAction: PropTypes.func.isRequired,
  primaryContactsEnabled: PropTypes.bool,
  publicConstantsLoading: PropTypes.bool.isRequired,
  fetchPublicConstants: PropTypes.func.isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }).isRequired,
};

export default enhance(RecoverPassword);



// WEBPACK FOOTER //
// ./src/components/Auth/RecoverPassword/index.jsx